import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  FormField,
  Input,
  Select,
  TextArea,
  DataTable,
  Skeleton,
  Tooltip,
  EmptyState,
  ErrorBoundary,
  FileUpload,
  Alert,
  useToast,
  ConfirmDialog,
  Toggle,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { categoryService } from '@/services/category.service';
import { productService } from '@/services/product.service';
import { uploadImageViaPresign } from './product-image-upload';
import { buildParentOptions } from './category-parent-link';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { ROLE_LEVELS } from '@/constants/product';
import { GLASS, GLASS_FORM, cn, toneClasses } from '@/constants/design-tokens';
import {
  validateCategoryName,
  stripHtml,
  CATEGORY_DESC_MAX,
  CATEGORY_IMAGE_ACCEPT,
  CATEGORY_IMAGE_MAX_SIZE,
} from '@/types/category';
import type { UpdateCategoryPayload } from '@/types/category';
import type { Product } from '@/types/product';

type ProductRow = Product & { id: string };
import { ApiError } from '@/lib/api-client';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatPaise } from '@/utils/currency';
import type { CurrencyCode } from '@bbpos/types';

// ─── Icons ──────────────────────────────────────────────────────────────────

function FolderIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function PackageIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getUserRoleLevel(role?: string): number {
  return ROLE_LEVELS[role ?? ''] ?? 0;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── KPI stat (inline, glass) ───────────────────────────────────────────────

function KpiMini({ label, value, tone, icon }: { label: string; value: string | number; tone: 'brand' | 'success' | 'violet' | 'teal'; icon: React.ReactNode }) {
  const tc = toneClasses(tone);
  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-4')}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tc.accent} pointer-events-none`} />
      <div className="relative flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', tc.icon)}>
          {icon}
        </div>
        <div>
          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">{value}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Product table columns ──────────────────────────────────────────────────

function buildProductColumns(currency: CurrencyCode): ColumnDef<ProductRow>[] {
  return [
  {
    key: 'name',
    header: 'Product',
    render: (_v, row) => (
      <div className="flex items-center gap-3">
        {row.image_url ? (
          <img src={row.image_url} alt={row.name} className="h-9 w-9 rounded-lg object-cover" />
        ) : (
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-100 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/20 flex items-center justify-center">
            <PackageIcon className="h-4 w-4 text-teal-500 dark:text-teal-400" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{row.name}</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">{row.sku}</p>
        </div>
      </div>
    ),
  },
  {
    key: 'stock_quantity',
    header: 'Stock',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">
        {Number(row.stock_quantity).toLocaleString('en-IN')} {row.unit}
      </span>
    ),
  },
  {
    key: 'price',
    header: 'Price',
    numeric: true,
    // A parent with variants carries no price of its own — its variants are
    // listed (and counted) separately.
    render: (_v, row) => row.has_variants ? (
      <span className="inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
        Has variants
      </span>
    ) : (
      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
        {formatPaise(row.price, currency)}
      </span>
    ),
  },
  {
    key: 'is_active',
    header: 'Status',
    render: (_v, row) => (
      <span className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium',
        row.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-400 dark:text-neutral-500',
      )}>
        <span className={cn(
          'h-2 w-2 rounded-full',
          row.is_active ? 'bg-emerald-500' : 'bg-neutral-300 dark:bg-neutral-600',
        )} />
        {row.is_active ? 'Active' : 'Inactive'}
      </span>
    ),
  },
  ];
}

// ─── CategoryDetailContent ──────────────────────────────────────────────────

function CategoryDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const profile = useAuthStore((s) => s.profile);
  const userRoleLevel = getUserRoleLevel(profile?.role);
  const currency = useTenantCurrency();
  const productColumns = useMemo(() => buildProductColumns(currency), [currency]);

  const canEdit = userRoleLevel >= ROLE_LEVELS.MANAGER;
  const canDelete = userRoleLevel >= ROLE_LEVELS.OWNER;

  const [isDirty, setIsDirty] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [productPage, setProductPage] = useState(1);
  const [imageUploading, setImageUploading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    parent_id: '',
    description: '',
    is_active: true,
    image_url: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { data: category, isLoading, error } = useQuery({
    queryKey: ['category', id],
    queryFn: () => categoryService.getById(id!),
    enabled: !!id,
  });

  const { data: allCategoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryService.list(),
    staleTime: 30_000,
  });

  // Active products only, at the selected location (products are one row per
  // location) — the same items the product count counts.
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['products', { category: category?.name, page: productPage, location: selectedLocationId }],
    queryFn: () =>
      productService.list({
        page: productPage, limit: 10, category: category?.name, is_active: 'true',
        ...(selectedLocationId ? { location_id: selectedLocationId } : {}),
      }),
    enabled: !!category?.name,
    staleTime: 30_000,
  });

  // Handle 404 — redirect to /categories with toast (spec §3.99)
  useEffect(() => {
    if (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        toast({ title: 'Category not found', variant: 'error' });
        navigate('/categories', { replace: true });
      }
    }
  }, [error, navigate, toast]);

  // Populate form when category loads
  useEffect(() => {
    if (category) {
      setForm({
        name: category.name,
        parent_id: category.parent_id ?? '',
        description: category.description ?? '',
        is_active: category.is_active,
        image_url: category.image_url ?? '',
      });
      setIsDirty(false);
    }
  }, [category]);

  // Ctrl+S to save, Escape to cancel
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty && canEdit) handleSave();
      }
      if (e.key === 'Escape' && isDirty) {
        handleCancel();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isDirty, form, canEdit]);

  function patch<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setIsDirty(true);
    if (formErrors[key]) setFormErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
  }

  function handleCancel() {
    if (category) {
      setForm({
        name: category.name,
        parent_id: category.parent_id ?? '',
        description: category.description ?? '',
        is_active: category.is_active,
        image_url: category.image_url ?? '',
      });
      setIsDirty(false);
      setFormErrors({});
    }
  }

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateCategoryPayload) => categoryService.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category', id] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setIsDirty(false);
      toast({ title: 'Category updated successfully', variant: 'success' });
    },
    onError: (err: Error) => {
      // CS3-005: a 409 is either the name clash or active products blocking the Active toggle.
      if (err instanceof ApiError && err.code === 'CATEGORY_HAS_ACTIVE_PRODUCTS') {
        toast({ title: 'Cannot deactivate category', description: err.message, variant: 'error' });
      } else if (err instanceof ApiError && err.statusCode === 409) {
        setFormErrors((prev) => ({ ...prev, name: 'A category with this name already exists at this level' }));
      } else {
        toast({ title: err.message, variant: 'error' });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => categoryService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast({ title: 'Category deactivated', variant: 'success' });
      navigate('/categories');
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.code === 'CATEGORY_HAS_ACTIVE_PRODUCTS') {
        setDeleteError(err.message);
        setShowDeleteDialog(false);
      } else {
        toast({ title: err.message, variant: 'error' });
      }
    },
  });

  const handleSave = useCallback(() => {
    const errs: Record<string, string> = {};
    const nameErr = validateCategoryName(form.name);
    if (nameErr) errs.name = nameErr;
    if (form.description && stripHtml(form.description).length > CATEGORY_DESC_MAX) {
      errs.description = `Description must be at most ${CATEGORY_DESC_MAX} characters`;
    }
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    updateMutation.mutate({
      name: stripHtml(form.name).trim(),
      parent_id: form.parent_id || null,
      description: stripHtml(form.description).trim() || null,
      image_url: form.image_url || null,
      is_active: form.is_active,
    });
  }, [form]);

  async function handleImageUpload(files: File[]) {
    if (!files.length) {
      patch('image_url', '');
      return;
    }
    const file = files[0];
    setImageUploading(true);
    try {
      const key = await uploadImageViaPresign(file, 'category_image');
      patch('image_url', key);
    } catch {
      toast({ title: 'Image upload failed', variant: 'error' });
    } finally {
      setImageUploading(false);
    }
  }

  const allCategories = allCategoriesData?.data ?? [];
  const parentOptions = [
    { value: '', label: '— None (root category) —' },
    ...buildParentOptions(allCategories, id),
  ];

  const subcategories = allCategories.filter((c) => c.parent_id === id);
  const productRows: ProductRow[] = (productsData?.data ?? []) as ProductRow[];

  // If error is a non-404, show error state (404 triggers redirect above)
  if (error && !(error instanceof ApiError && error.statusCode === 404)) {
    return (
      <PageContainer
        title="Category"
        breadcrumbs={[{ label: 'Categories', href: '/categories' }, { label: 'Error' }]}
      >
        <Alert
          variant="danger"
          title="Failed to load category"
          description="An error occurred. Please try refreshing."
          action={{ label: 'Retry', onClick: () => queryClient.invalidateQueries({ queryKey: ['category', id] }) }}
        />
      </PageContainer>
    );
  }

  if (isLoading || !category) {
    return (
      <PageContainer
        title="Category"
        breadcrumbs={[{ label: 'Categories', href: '/categories' }, { label: '...' }]}
      >
        <div className="space-y-4 animate-fade-in">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Skeleton variant="rect" height={90} />
            <Skeleton variant="rect" height={90} />
            <Skeleton variant="rect" height={90} className="hidden lg:block" />
          </div>
          <Skeleton variant="rect" height={300} />
        </div>
      </PageContainer>
    );
  }

  // Build breadcrumb path for parent
  const parentCategory = category.parent_id ? allCategories.find((c) => c.id === category.parent_id) : null;
  const breadcrumbs = [
    { label: 'Categories', href: '/categories' },
    ...(parentCategory ? [{ label: parentCategory.name, href: `/categories/${parentCategory.id}` }] : []),
    { label: category.name },
  ];

  return (
    <PageContainer
      title={category.name}
      subtitle={category.description ?? undefined}
      breadcrumbs={breadcrumbs}
      actions={
        <div className="flex items-center gap-2">
          {isDirty && canEdit && (
            <>
              <Button variant="secondary" size="sm" onClick={handleCancel} disabled={updateMutation.isPending}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave} loading={updateMutation.isPending}>
                Save Changes
              </Button>
            </>
          )}
          {canDelete && (
            <Tooltip content="Deactivate category">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                aria-label="Deactivate category"
              >
                <TrashIcon />
                <span className="ml-1.5 hidden sm:inline">Deactivate</span>
              </Button>
            </Tooltip>
          )}
        </div>
      }
    >
    <div className="animate-fade-in space-y-6">
      {/* ─── Deactivate error alert ────────────────────────────────── */}
      {deleteError && (
        <Alert
          variant="danger"
          title="Cannot deactivate category"
          description={deleteError}
          dismissible
          onDismiss={() => setDeleteError(null)}
        />
      )}

      {/* ─── Stats ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 animate-slide-up">
        <KpiMini
          label="Products"
          value={category.product_count.toLocaleString('en-IN')}
          tone="brand"
          icon={<PackageIcon className="h-5 w-5" />}
        />
        <KpiMini
          label="Subcategories"
          value={subcategories.length}
          tone="violet"
          icon={<FolderIcon className="h-5 w-5" />}
        />
        <KpiMini
          label="Last Modified"
          value={formatRelativeTime(category.updated_at)}
          tone="teal"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* ─── Edit Form (Glass) ─────────────────────────────────────── */}
      <div className={cn(GLASS_FORM, 'p-6')}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Category Details</h3>
          {isDirty && (
            <span className="text-xs text-warning-600 dark:text-warning-400 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-warning-500 inline-block animate-pulse" />
              Unsaved changes · Ctrl+S to save
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Image */}
          <div className="lg:col-span-1">
            <FormField label="Category Image">
              {form.image_url ? (
                <div className="relative rounded-xl overflow-hidden">
                  <img src={form.image_url} alt="Category preview" className="w-full h-40 object-cover rounded-xl" />
                  {canEdit && (
                    <button
                      type="button"
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 dark:bg-neutral-900/90 text-neutral-600 dark:text-neutral-300 hover:bg-white dark:hover:bg-neutral-900 shadow transition-colors"
                      onClick={() => patch('image_url', '')}
                      aria-label="Remove image"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              ) : canEdit ? (
                <FileUpload
                  variant="dropzone"
                  accept={CATEGORY_IMAGE_ACCEPT}
                  maxSize={CATEGORY_IMAGE_MAX_SIZE}
                  onUpload={handleImageUpload}
                  loading={imageUploading}
                />
              ) : (
                <div className="flex h-40 w-full items-center justify-center rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/10">
                  <FolderIcon className="h-12 w-12 text-indigo-300 dark:text-indigo-700" />
                </div>
              )}
            </FormField>
          </div>

          {/* Right: Fields */}
          <div className="lg:col-span-2 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Name" required error={formErrors.name}>
                <Input
                  value={form.name}
                  onChange={(v) => patch('name', v)}
                  placeholder="Category name"
                  disabled={!canEdit}
                />
              </FormField>

              <FormField label="Parent Category">
                <Select
                  options={parentOptions}
                  value={form.parent_id}
                  onChange={(v) => patch('parent_id', typeof v === 'string' ? v : '')}
                  placeholder="None (root)"
                  disabled={!canEdit}
                />
              </FormField>
            </div>

            <FormField label="Description" error={formErrors.description}>
              <TextArea
                value={form.description}
                onChange={(v) => patch('description', v)}
                placeholder="Optional description..."
                rows={2}
                maxLength={CATEGORY_DESC_MAX}
                resize="none"
                disabled={!canEdit}
              />
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Status">
                <div className="flex items-center h-10">
                  <Toggle
                    checked={form.is_active}
                    onChange={(v) => patch('is_active', v)}
                    label={form.is_active ? 'Active' : 'Inactive'}
                    disabled={!canEdit}
                  />
                </div>
              </FormField>
            </div>

            {/* Metadata */}
            <div className="flex items-center gap-4 pt-2 text-xs text-neutral-400 dark:text-neutral-500">
              <span>Created {new Date(category.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              <span>·</span>
              <span>Updated {formatRelativeTime(category.updated_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Subcategories ─────────────────────────────────────────── */}
      {/* Shown to editors on a leaf too: "Add Subcategory" is how a new level starts. */}
      {(subcategories.length > 0 || canEdit) && (
        <div className={cn(GLASS, 'p-6')}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
              Subcategories ({subcategories.length})
            </h3>
            {canEdit && (
              <Button variant="secondary" size="sm" onClick={() => navigate(`/categories?parent=${id}`)}>
                Add Subcategory
              </Button>
            )}
          </div>
          {subcategories.length === 0 && (
            <p className="text-sm text-neutral-400 dark:text-neutral-500">No subcategories yet.</p>
          )}
          <div className="space-y-1">
            {subcategories
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => navigate(`/categories/${sub.id}`)}
                  className={cn(
                    'flex items-center justify-between w-full p-3 rounded-xl text-left group transition-all duration-200',
                    'hover:bg-white/60 dark:hover:bg-neutral-800/60',
                  )}
                >
                  <div className="flex items-center gap-3">
                    {sub.image_url ? (
                      <img src={sub.image_url} alt="" className="h-8 w-8 rounded-lg object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-100 to-indigo-50 dark:from-violet-900/30 dark:to-indigo-900/20 flex items-center justify-center">
                        <FolderIcon className="h-4 w-4 text-violet-400 dark:text-violet-500" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                        {sub.name}
                      </p>
                      <p className="text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">{sub.product_count} products</p>
                    </div>
                  </div>
                  <svg className="h-4 w-4 text-neutral-300 dark:text-neutral-600 group-hover:text-brand-400 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* ─── Products in Category ──────────────────────────────────── */}
      <div className={cn(GLASS, 'p-6')}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            Products in this Category
          </h3>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/products?category=${encodeURIComponent(category.name)}`)}
          >
            View All
          </Button>
        </div>
        {productsLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rect" height={48} />
            ))}
          </div>
        ) : productRows.length === 0 ? (
          <EmptyState
            variant="table"
            icon={
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20">
                <PackageIcon className="h-6 w-6 text-teal-500" />
              </div>
            }
            title="No products in this category"
            description="Assign products to this category from the Products page"
          />
        ) : (
          <DataTable<ProductRow>
            columns={productColumns}
            data={productRows}
            variant="compact"
            totalCount={productsData?.meta?.total}
            currentPage={productPage}
            pageSize={10}
            onPageChange={setProductPage}
          />
        )}
      </div>

      {/* ─── Deactivate Dialog ─────────────────────────────────────── */}
      <ConfirmDialog
        open={showDeleteDialog}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={() => deleteMutation.mutate()}
        title={`Deactivate "${category.name}"?`}
        description={
          (category.product_count ?? 0) > 0
            ? `Move or deactivate its ${category.product_count} product${category.product_count !== 1 ? 's' : ''} first.`
            : 'It stays in the list as Inactive, and you can turn it back on.'
        }
        confirmLabel="Deactivate"
        confirmDisabled={(category.product_count ?? 0) > 0}
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
    </PageContainer>
  );
}

// ─── Export with ErrorBoundary ───────────────────────────────────────────────

export function CategoryDetailPage() {
  return (
    <ErrorBoundary>
      <CategoryDetailContent />
    </ErrorBoundary>
  );
}
